import React, { cloneElement, useCallback } from 'react';
import times from 'lodash/times';
import { useTranslation } from '../../utils/useTranslation';
import { IconClose } from '../icon/IconClose';
import { IconPlus } from '../icon/IconPlus';
import { Button } from '../input/Button';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { CategoryUI } from './types';

interface CategoryBadgeProps {
    name: string;
    onRemove: VoidFunction;
}

const CategoryBadge = (props: CategoryBadgeProps) => (
    <Flex
        spacing={1}
        direction="row"
        px={2}
        height={32}
        alignItems="center"
        borderRadius={2.5}
        border="1px solid var(--placeholder)"
        textTransform="uppercase"
    >
        <Typography cursor="unset" weight="medium" variant="caption">
            {props.name}
        </Typography>
        <IconClose
            color="var(--color-primary)"
            cursor="pointer"
            fontWeight="bold"
            size={16}
            onClick={props.onRemove}
        />
    </Flex>
);

interface BusinessCategoriesUIProps {
    isLoading: boolean;
    businessCategories?: Record<number, CategoryUI>;
    onRemove: (categoryId: number) => void;
    onOpenSelectCategoriesDialog: VoidFunction;
}

export const BusinessCategoriesUI = (props: BusinessCategoriesUIProps) => {
    const t = useTranslation();
    const { isLoading, businessCategories = {}, onRemove, onOpenSelectCategoriesDialog } = props;

    const businessCategoriesArray = Object.entries(businessCategories);
    const onRemoveCallback = useCallback(
        (categoryId: number) => {
            onRemove(categoryId);
        },
        [onRemove],
    );

    return (
        <Flex
            width="100%"
            spacing={0.5}
            px={2}
            height={{ xs: '100%', sm: 'auto' }}
            borderRadius={2}
            backgroundColor="var(--background-primary)"
        >
            <Typography variant="title3" weight="bold" py={2}>
                {t('Категория бизнеса')}
            </Typography>
            <Typography variant="body2" weight="regular" pb={2}>
                {t(
                    'Выберите категорию, которая лучше всего описывает сферу вашего бизнеса. Так пользователи быстрее вас найдут.',
                )}
            </Typography>

            <Flex pb={2} direction="row" spacing={1} flexWrap="wrap">
                <Button
                    disabled={isLoading}
                    color="secondary"
                    spacing={1}
                    onClick={onOpenSelectCategoriesDialog}
                    type="button"
                >
                    <IconPlus size={16} />
                    <Box display="inline">{t('Добавить категорию')}</Box>
                </Button>
                {times(businessCategoriesArray.length, (index) =>
                    cloneElement(
                        <CategoryBadge
                            key={businessCategoriesArray[index][1].id}
                            name={businessCategoriesArray[index][1].name}
                            onRemove={() => {
                                onRemoveCallback(businessCategoriesArray[index][1].id);
                            }}
                        />,
                    ),
                )}
            </Flex>
        </Flex>
    );
};
